import { supabase } from '../lib/supabase';
import { NotificationService } from './notificationService';

export type SupportedEntityType = 'production' | 'sample' | 'express_order' | 'express_sample';

export interface ChangeRecord {
  field: string;
  oldValue: any;
  newValue: any;
}

export interface LogAndNotifyParams<T extends Record<string, any>> {
  entityType: SupportedEntityType;
  entityId: string;
  entityName: string;
  previous: T;
  next: T;
  changedByUserId?: string | null;
  includeFields?: string[]; // si se especifica, solo compara estos campos
}

export const diffObject = (prev: any, next: any, include?: string[]): ChangeRecord[] => {
  const fields = include && include.length > 0 ? include : Array.from(new Set([...Object.keys(prev || {}), ...Object.keys(next || {})]));
  const changes: ChangeRecord[] = [];
  for (const field of fields) {
    const oldValue = prev ? prev[field] : undefined;
    const newValue = next ? next[field] : undefined;
    const normalizedOld = oldValue === undefined ? null : oldValue;
    const normalizedNew = newValue === undefined ? null : newValue;
    if (JSON.stringify(normalizedOld) !== JSON.stringify(normalizedNew)) {
      changes.push({ field, oldValue: normalizedOld, newValue: normalizedNew });
    }
  }
  return changes;
};

export const logChangesAndNotify = async <T extends Record<string, any>>(params: LogAndNotifyParams<T>): Promise<void> => {
  const { entityType, entityId, entityName, previous, next, changedByUserId, includeFields } = params;
  const changes = diffObject(previous, next, includeFields);
  if (changes.length === 0) return;

  // Obtener email del usuario si es posible
  let changedByEmail: string | null = null;
  try {
    const { data: user } = await supabase.auth.getUser();
    changedByEmail = user?.user?.email ?? null;
  } catch {}

  // Guardar en historial
  await supabase.from('entity_change_log').insert({
    entity_type: entityType,
    entity_id: entityId,
    entity_name: entityName,
    changed_by: changedByUserId || null,
    changed_by_email: changedByEmail,
    changes: changes as any
  });

  // Componer mensaje legible con nombres de campos amigables
  const summary = changes
    .map(c => `${getFieldLabel(c.field)}: ${formatChangeMessage(c.field, c.oldValue, c.newValue)}`)
    .join(' | ');

  // Notificar a Producción + Admins
  await NotificationService.notifyProductionUsers({
    title: labelByType(entityType) + ' actualizado',
    message: `${entityName}: ${summary}`,
    type: mapType(entityType),
    entity_type: mapType(entityType),
    entity_id: entityId,
    action_url: routeByType(entityType)
  });
};

const mapType = (t: SupportedEntityType): 'production' | 'sample' | 'express' | 'express_sample' => {
  if (t === 'express_order') return 'express';
  return t;
};

const labelByType = (t: SupportedEntityType): string => {
  switch (t) {
    case 'production': return 'Producción';
    case 'sample': return 'Muestra';
    case 'express_order': return 'Express';
    case 'express_sample': return 'Muestra Express';
  }
};

const routeByType = (t: SupportedEntityType): string => {
  switch (t) {
    case 'production': return '/productions';
    case 'sample': return '/samples';
    case 'express_order': return '/express';
    case 'express_sample': return '/expresssamples';
  }
};

const getFieldLabel = (field: string): string => {
  const fieldLabels: Record<string, string> = {
    // Production fields
    'name': 'Nombre',
    'client_id': 'Cliente',
    'salesperson': 'Vendedor',
    'delivery_date': 'Fecha de entrega',
    'status': 'Estado',
    'priority': 'Prioridad',
    'notes': 'Notas',
    'approved_file': 'Archivo de ficha aprobada',
    'production_file': 'Archivo de producción',
    'finished_at': 'Fecha de terminación',
    
    // Sample fields
    'sample_type': 'Tipo de muestra',
    'sample_status': 'Estado de muestra',
    'sample_priority': 'Prioridad de muestra',
    'sample_notes': 'Notas de muestra',
    'sample_file': 'Archivo de muestra',
    
    // Express fields
    'express_value': 'Valor express',
    'express_notes': 'Notas express',
    'express_file': 'Archivo express',
    
    // Express Sample fields
    'sample_value': 'Valor de muestra',
    'sample_express_notes': 'Notas de muestra express',
    'sample_express_file': 'Archivo de muestra express',
    
    // Common fields
    'created_at': 'Fecha de creación',
    'updated_at': 'Fecha de actualización',
    'file_names': 'Archivos'
  };
  
  return fieldLabels[field] || field;
};

const formatChangeMessage = (field: string, oldValue: any, newValue: any): string => {
  // Caso especial para archivos aprobados
  if (field === 'approved_file') {
    if (oldValue && newValue) {
      return 'Se reemplazó el archivo de la ficha aprobada';
    } else if (newValue && !oldValue) {
      return 'Se agregó archivo de ficha aprobada';
    } else if (!newValue && oldValue) {
      return 'Se eliminó archivo de ficha aprobada';
    }
  }
  
  // Caso especial para otros archivos
  if (field.includes('_file') || field === 'file_names') {
    if (oldValue && newValue) {
      return 'Se reemplazó el archivo';
    } else if (newValue && !oldValue) {
      return 'Se agregó archivo';
    } else if (!newValue && oldValue) {
      return 'Se eliminó archivo';
    }
  }
  
  // Caso general
  return `${formatValue(oldValue)} → ${formatValue(newValue)}`;
};

const formatValue = (v: any): string => {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'string' && v.length > 60) return v.slice(0, 57) + '…';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};


